import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { formatNumber, getISO2ByISO3, getNameByISO3 } from "../../../../libs/misc";
import useFetch from "../../../../libs/useFetch";
import DataTable from "../../../../../ui-kit/DataTable/DataTable";
import useAxiosPrivate from "../../../../libs/useAxiosPrivate";
import { getCommodities } from "../../home/core/_request";
import { group } from "console";
import { Link } from "react-router-dom";
import { Form } from "react-bootstrap";
import { getCommoditiesDescription } from "../core/_requests";

interface DataRow {
  hs_code: string;
  desc?:string;
  tw_tariff: number;
  sa_tariff: number;
  import_from_khm: number;
  total: number
}

// Re-introduce columnHelper from @tanstack/react-table for defining columns
const columnHelper = createColumnHelper<DataRow>();

interface TradePotentialByProductTableProps {
  digit?: any;
  modalHandler?: (data: any, hsCode:string) => void;
}

const TradePotentialByProductTable: FC<TradePotentialByProductTableProps> = ({ digit, modalHandler }) => {
  const [dataTable, setDataTable] = useState<DataRow[]>([]);
  const [rawData, setRawData] = useState<any[]>([]);
  const { fetchCsvData } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [includeZero, setIncludeZero] = useState(false);
  const axiosPrivate = useAxiosPrivate()
  const hsMap = new Map<string, string>()
  
  //Get Data from CSV File or API
  useEffect(() => {
    setIsLoading(true);
    fetchCsvData('data/trade-potential/KHM.csv', setRawData)
  }, []);

  const grouped = useMemo(() => {
    const map: Record<string, any[]> = {};
    if (!rawData?.length) return map;
  
    const filtered = rawData.filter(x => x.par === 'KHM' && x.tariff !== null);
    filtered.forEach(item => {
      const key = item.hs?.substring(0, digit.value);
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    return map;
  }, [rawData, digit]);

  //Transform raw data into the format expected by DataTable
  useEffect(()=>{
    setIsLoading(true);
    if (rawData.length === 0) return;
    if (!Object.keys(grouped).length) return;
    const hsCodes = Object.keys(grouped)
    getCommoditiesDescription(axiosPrivate,{
      hs_codes:hsCodes
    })
    .then((res)=>{
      const tradeData = res.data.hs_list
      tradeData.forEach((item: any) => {
        hsMap.set(String(item.id), item.description);
      });
      
      const summarised:DataRow[] = Object.entries(grouped).map(([hs, items]) => {
        const itemsArray = items as Array<any>;
        const hsDescription = hsMap.get(hs) || '-';
        const totalVal = itemsArray.reduce((sum, d) => sum + d?.imports, 0);
        const weightedTariff = itemsArray.reduce((sum, d) => sum + d.tariff * d.imports, 0) / totalVal;
        const averageTariff = itemsArray.reduce((sum, d) => sum + d.tariff, 0) / itemsArray.length;
        const totalChange = itemsArray.reduce((sum, d) => sum + d.tot, 0)
        const importTotal = totalVal;

        return {
          hs_code: hs,
          desc:hsDescription,
          tw_tariff: weightedTariff,
          sa_tariff: averageTariff,
          import_from_khm: importTotal,
          total: totalChange
        };
      });
    
      setDataTable(summarised);
    }).catch(e=>{
      console.error(e)
    }).finally(()=>{
      setIsLoading(false);
    })      
  }, [rawData, digit]);

  const renderHsCell = (info: any) => {
    const hsCode = info.getValue();
    // const isClickable = String(hsCode).length === 6;
    const isClickable = true
  
    return isClickable ? (
      <span
        className="badge cursor-pointer text-primary fw-1000"
        style={{ background: 'rgba(108, 117, 125, 0.1)' }}
        onClick={() => modalHandler && modalHandler(grouped[hsCode], hsCode)}
      >
        {hsCode}
      </span>
    ) : (
      <span
        className="badge text-primary fw-1000"
        style={{ background: 'rgba(108, 117, 125, 0.1)' }}
      >
        {hsCode}
      </span>
    );
  };

  // 3. Define Columns for the DataTable using columnHelper
  const columns: ColumnDef<DataRow, any>[] = useMemo(() => [
    columnHelper.accessor('hs_code', {
      header: 'Commodity',
      cell: renderHsCell,
      meta: {
        headerClass: 'text-center',
        cellClass: 'text-center font-semibold text-dark',
      },
      enableSorting: true,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor('desc', {
      header: 'Commodity Description',
      cell: info => info.getValue(),
      meta: {
        headerClass: 'text-center',
        cellClass: 'text-start font-semibold text-dark',
      },
      enableSorting: true,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor('tw_tariff', {
      header: 'Trade Weight Tariff',
      cell: info => `${Math.round(info.getValue())}%`,
      meta: {
        cellClass: 'text-end',
        headerClass: 'font-bold'
      },
      enableSorting: true,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor('sa_tariff', {
      header: 'Average Tariff',
      cell: info => `${Math.round(info.getValue())}%`,
      meta: {
        cellClass: 'text-end',
        headerClass: "font-bold"
      },
      enableSorting: true,
      enableGlobalFilter: false,
    }),
    columnHelper.accessor('import_from_khm', {
      header: 'Import from KHM',
      cell: info => formatNumber(Math.round(info.getValue())),
      meta: {
        cellClass: 'text-end',
        headerClass: "font-bold"
      },
      enableSorting: true,
      enableGlobalFilter: false,
    }),
    columnHelper.accessor('total', {
      header: 'Total Potential Change',
      cell: info => {
        const value = Math.ceil(info.getValue());
        if(value>0){
          return (<span className="text-success fw-bolder">+{formatNumber(value)}</span>)
        }else if(value<0){
          return (<span className="text-danger fw-bolder">-{formatNumber(value)}</span>)
        }
        return (<span className="fw-bolder">{formatNumber(value)}</span>)
      },
      meta: {
        headerClass: 'font-bold',
        cellClass: 'text-end',
      },
      filterFn: (row, columnId, filterValue) => {
        const value = row.getValue<number>(columnId);
        return filterValue === true // includeZero
          ? true                     // show everything
          : value !== 0;             // exclude zero values      
      },
      enableSorting: true,
      enableGlobalFilter: false,
    }),
  ], [grouped,includeZero]); // Columns don't depend on state, so empty dependency array is fine here

  
  return (
    <>
      <Form.Check
        type="switch"
        id="partner-table-zero-toggle"
        label="Include total potential change of zero"
        checked={includeZero}
        onChange={(e) => setIncludeZero(e.target.checked)}
        className="form-switch form-switch-sm mb-1 mt-10"
        style={{ minHeight: "1.5rem" }}
      />
      <DataTable<DataRow>
        data={dataTable}
        columns={columns}
        isLoading={isLoading}
        enablePagination={true}
        enableSorting={true}
        enableGlobalFilter={true}
        globalFilterPlaceholder="Search by name, city, or status..."
        defaultPageSize={20}
        pageSizes={[5, 10, 20, 50, 100]}
        className="my-custom-table-wrapper"
        initialSortBy={{ id: 'import_from_khm', desc: true }} 
        initialColumnFilters={[
          {
            id: 'total',
            value: includeZero 
          }
        ]} 
      />
    </>
  );
};

export default TradePotentialByProductTable;
